import { useEffect, useState } from "react";

/**
 * Branded splash — pulses the logo softly with a gold shimmer.
 * Shows while auth check runs; guarantees a minimum display time so it doesn't flash.
 */
export default function Splash({ show }) {
  const [render, setRender] = useState(show);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (show) {
      setRender(true);
      setFading(false);
      return;
    }
    setFading(true);
    const t = setTimeout(() => setRender(false), 500);
    return () => clearTimeout(t);
  }, [show]);

  if (!render) return null;
  return (
    <div
      className={`fixed inset-0 z-[9999] brand-gradient flex items-center justify-center transition-opacity duration-500 ${
        fading ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      aria-hidden="true"
      data-testid="splash-screen"
    >
      {/* Gold shimmer aura */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="h-72 w-72 rounded-full opacity-40 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, hsl(41 65% 55% / 0.6) 0%, transparent 70%)",
            animation: "okeydas-glow 3s ease-in-out infinite",
          }}
        />
      </div>
      {/* Sparkles around */}
      <span className="absolute top-[28%] left-[18%] text-accent text-2xl opacity-70 animate-pulse" style={{ animationDelay: "0.2s" }}>✦</span>
      <span className="absolute top-[22%] right-[22%] text-accent text-lg opacity-60 animate-pulse" style={{ animationDelay: "0.6s" }}>✦</span>
      <span className="absolute bottom-[30%] left-[24%] text-accent text-xl opacity-60 animate-pulse" style={{ animationDelay: "1s" }}>✦</span>
      <span className="absolute bottom-[26%] right-[16%] text-accent text-2xl opacity-70 animate-pulse" style={{ animationDelay: "1.4s" }}>✦</span>

      <div className="relative flex flex-col items-center gap-6 px-8">
        <img
          src="/brand/logo.png"
          alt="Okeydaş"
          draggable={false}
          className="w-64 max-w-[70vw] h-auto select-none"
          style={{
            animation: "okeydas-pulse 2.4s ease-in-out infinite",
            filter: "drop-shadow(0 12px 32px rgba(0,0,0,0.35))",
          }}
        />
        <p
          className="text-primary-foreground/85 font-display font-semibold text-center text-sm tracking-wide"
          style={{ animation: "okeydas-fadein 1s ease-out both", animationDelay: "0.3s" }}
        >
          Masayı kur, arkadaşlarını çağır,<br />
          <span className="text-accent">skorları Okeydaş tutsun.</span>
        </p>

        {/* Loading pips */}
        <div className="flex gap-2 mt-2" aria-label="Yükleniyor">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-accent"
              style={{
                animation: "okeydas-blink 1.2s ease-in-out infinite",
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes okeydas-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.045); }
        }
        @keyframes okeydas-glow {
          0%, 100% { transform: scale(1); opacity: 0.35; }
          50% { transform: scale(1.15); opacity: 0.55; }
        }
        @keyframes okeydas-fadein {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes okeydas-blink {
          0%, 100% { opacity: 0.25; transform: scale(0.85); }
          50% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
